/**
 * Carry-over Defaults
 *
 * The product-level settings the carry-over rule reads on a TARGET area, and
 * the global defaults that apply when a product carries none:
 *
 * - Product class (TEXTILE or OBJECT), pre-filled from the Shirtigo shipping
 *   category and editable by an admin. Decides the class-change rules
 *   (object master onto textile, textile master onto object) and the
 *   default anchor.
 * - Carry-over percent (1-100): the share of the print area a derived,
 *   unedited motif is fitted into, centered. A TARGET size, not a cap.
 * - Vertical anchor: TOP with an offset in mm from the top edge, or CENTER.
 *
 * The two global numbers are constants on purpose: a settings table for two
 * values is not worth its weight until the first time they have to change.
 */

export type ProductClass = 'TEXTILE' | 'OBJECT';

export const PRODUCT_CLASSES: readonly ProductClass[] = ['TEXTILE', 'OBJECT'];

export type CarryOverAnchor = 'TOP' | 'CENTER';

export const CARRY_OVER_ANCHORS: readonly CarryOverAnchor[] = ['TOP', 'CENTER'];

/** Percent of a textile area an OBJECT master's motif is fitted into (rule 3a). */
export const OBJECT_TO_TEXTILE_PERCENT = 80;

/** Distance of the box's top edge from the area's top edge for anchor TOP. */
export const DEFAULT_TOP_OFFSET_MM = 20;

export const MIN_CARRY_OVER_PERCENT = 1;
export const MAX_CARRY_OVER_PERCENT = 100;

/**
 * Shirtigo shipping categories (`Product.shippingCategory`) that are worn:
 * 1 shirts, 2 polos, 3 sweats/hoodies/zips/trousers/aprons, 29 and 31
 * embroidered textiles.
 */
export const TEXTILE_SHIPPING_CATEGORIES: readonly number[] = [1, 2, 3, 29, 31];

/**
 * Shipping categories that are objects: 7 bags, 9/11/19 posters and canvas,
 * 13 mugs and bottles, 15 doormats, 17 pillows, 21/25/27 caps and beanies,
 * 23 phone cases.
 */
export const OBJECT_SHIPPING_CATEGORIES: readonly number[] = [
  7, 9, 11, 13, 15, 17, 19, 21, 23, 25, 27,
  // 33 cutting boards, 35 glasses: objects as well
  33, 35,
];

export function isProductClass(value: unknown): value is ProductClass {
  return (
    typeof value === 'string' &&
    (PRODUCT_CLASSES as readonly string[]).includes(value)
  );
}

export function isCarryOverAnchor(value: unknown): value is CarryOverAnchor {
  return (
    typeof value === 'string' &&
    (CARRY_OVER_ANCHORS as readonly string[]).includes(value)
  );
}

/**
 * The class a shipping category pre-fills. `null` for an unknown or missing
 * category: such a product has no class until an admin sets one, and the
 * class-change rules do not apply to it.
 */
export function productClassForShippingCategory(
  shippingCategory: number | null | undefined
): ProductClass | null {
  if (typeof shippingCategory !== 'number') return null;
  if (TEXTILE_SHIPPING_CATEGORIES.includes(shippingCategory)) return 'TEXTILE';
  if (OBJECT_SHIPPING_CATEGORIES.includes(shippingCategory)) return 'OBJECT';
  return null;
}

/**
 * The anchor that applies when the product (and area) set none: textiles
 * hang from the top (a motif sits where a chest print sits), objects and
 * products of unknown class are centered.
 */
export function defaultAnchorForClass(
  productClass: ProductClass | null | undefined
): CarryOverAnchor {
  return productClass === 'TEXTILE' ? 'TOP' : 'CENTER';
}

/** A percent value the rule accepts: an integer between 1 and 100. */
export function isValidCarryOverPercent(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_CARRY_OVER_PERCENT &&
    value <= MAX_CARRY_OVER_PERCENT
  );
}

/** The carry-over fields as a product or a print area carries them. */
export interface CarryOverFields {
  carryOverPercent?: number | null;
  carryOverAnchor?: CarryOverAnchor | null;
  carryOverTopOffsetMm?: number | null;
}

export interface CarryOverProductFields extends CarryOverFields {
  productClass?: ProductClass | null;
}

/** The fields a placement area takes from product and area together. */
export interface EffectiveCarryOver {
  productClass?: ProductClass | null;
  carryOverPercent?: number;
  carryOverAnchor?: CarryOverAnchor;
  carryOverTopOffsetMm?: number;
}

/**
 * The settings that apply to one print area: an area value overrides the
 * product's, `null` on the area inherits. The class is the product's. Only
 * set fields are returned, so the result spreads into a `PlacementArea`
 * without turning "inherit" into "unset" (the rule then falls back to the
 * class default for anchor and offset).
 */
export function effectiveCarryOver(
  product: CarryOverProductFields | null | undefined,
  area: CarryOverFields | null | undefined
): EffectiveCarryOver {
  const out: EffectiveCarryOver = {};
  if (product?.productClass !== undefined) {
    out.productClass = product.productClass;
  }
  const percent = area?.carryOverPercent ?? product?.carryOverPercent;
  if (percent != null) out.carryOverPercent = percent;
  const anchor = area?.carryOverAnchor ?? product?.carryOverAnchor;
  if (anchor != null) out.carryOverAnchor = anchor;
  const offset = area?.carryOverTopOffsetMm ?? product?.carryOverTopOffsetMm;
  if (offset != null) out.carryOverTopOffsetMm = offset;
  return out;
}
